<script setup>
import {computed, onUnmounted, ref, watch} from "vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import {useTenantTextRules} from "@/helpers";

const props = defineProps({
  existingDocument: {
    type: Object,
    default: null,
  },
  licenseDocumentError: {
    type: String,
    default: "",
  },
  removeLicenseDocumentError: {
    type: String,
    default: "",
  },
  markedForRemoval: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["file-selected", "remove", "undo-remove"]);

const {resolveTextRule} = useTenantTextRules();
const fileInput = ref(null);
const selectedFile = ref(null);
const selectedPreviewUrl = ref(null);
const previewModalOpen = ref(false);

const fieldLabel = () =>
  resolveTextRule(
    "company.form.business_license_or_drivers_license",
    "Business License or Current Drivers License",
    "company.form.business_license_or_drivers_license"
  );

const uploadHint = () =>
  resolveTextRule(
    "company.form.business_license_or_drivers_license_hint",
    "Upload a clear photo or PDF of your business license or current driver's license.",
    "company.form.business_license_or_drivers_license_hint"
  );

const viewLabel = () =>
  resolveTextRule(
    "company.form.business_license_or_drivers_license_view",
    "View",
    "company.form.business_license_or_drivers_license_view"
  );

const formatUploadTimestamp = (createddate) => {
  if (createddate == null || createddate === "") {
    return "";
  }

  const sec = Number(createddate);
  if (!Number.isFinite(sec)) {
    return "";
  }

  return new Date(sec * 1000).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const inferTypeFromFile = (file) => {
  if (!file) {
    return "F";
  }

  if (file.type?.startsWith("image/")) {
    return "I";
  }

  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";

  return ["png", "jpg", "jpeg", "gif", "svg", "webp", "ico", "bmp", "heic", "heif"].includes(extension)
    ? "I"
    : "F";
};

const isImageDocument = (document) => document?.type === "I";
const isFileDocument = (document) => document?.type === "F";

const revokeSelectedPreview = () => {
  if (selectedPreviewUrl.value) {
    URL.revokeObjectURL(selectedPreviewUrl.value);
    selectedPreviewUrl.value = null;
  }
};

const showExistingDocument = computed(
  () => !!props.existingDocument && !props.markedForRemoval && !selectedFile.value
);

const pendingDocument = computed(() => {
  if (!selectedFile.value) {
    return null;
  }

  return {
    file_name: selectedFile.value.name,
    url: selectedPreviewUrl.value,
    type: inferTypeFromFile(selectedFile.value),
    createddate: null,
    pending: true,
  };
});

const displayedDocument = computed(() => {
  if (pendingDocument.value) {
    return pendingDocument.value;
  }

  if (showExistingDocument.value) {
    return props.existingDocument;
  }

  return null;
});

watch(
  () => props.existingDocument?.file_name,
  () => {
    if (!selectedFile.value) {
      return;
    }

    clearSelection();
  }
);

onUnmounted(() => {
  revokeSelectedPreview();
});

const openFilePicker = () => {
  fileInput.value?.click();
};

const onFileInputChange = () => {
  const file = fileInput.value?.files?.[0];
  if (!file) {
    return;
  }

  revokeSelectedPreview();
  selectedFile.value = file;
  selectedPreviewUrl.value = URL.createObjectURL(file);
  emit("file-selected", file);
};

const clearSelection = () => {
  selectedFile.value = null;
  revokeSelectedPreview();

  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const removeExisting = () => {
  clearSelection();
  emit("remove");
};

const undoRemove = () => {
  clearSelection();
  emit("undo-remove");
};

const openPreviewModal = () => {
  if (displayedDocument.value && isImageDocument(displayedDocument.value)) {
    previewModalOpen.value = true;
  }
};

const closePreviewModal = () => {
  previewModalOpen.value = false;
};

const openDocumentInNewTab = () => {
  const url = displayedDocument.value?.url;
  if (!url || typeof window === "undefined") {
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
};
</script>

<template>
  <div class="sm:col-span-2 w-full min-w-0 space-y-3">
    <InputLabel :value="fieldLabel()" />
    <p class="text-xs text-gray-500">{{ uploadHint() }}</p>

    <input
      ref="fileInput"
      type="file"
      accept="image/*,.pdf,.heic,.heif"
      class="sr-only"
      @change="onFileInputChange"
    />

    <figure
      v-if="displayedDocument"
      class="relative w-full max-w-full sm:max-w-sm rounded-lg border border-gray-200 bg-gray-50/40 p-3"
    >
      <figcaption class="w-full mb-2">
        <div class="text-sm font-medium text-gray-800 break-all" v-text="displayedDocument.file_name"></div>
        <div v-if="formatUploadTimestamp(displayedDocument.createddate)" class="text-xs text-gray-500">
          {{ formatUploadTimestamp(displayedDocument.createddate) }}
        </div>
        <div v-else-if="displayedDocument.pending" class="text-xs text-amber-700">
          Pending save
        </div>
      </figcaption>

      <img
        v-if="isImageDocument(displayedDocument) && displayedDocument.url"
        :src="displayedDocument.url"
        :alt="displayedDocument.file_name"
        class="h-auto max-h-72 w-full max-w-full rounded-lg cursor-pointer border border-gray-200 bg-white object-contain"
        @click="openPreviewModal"
      />

      <a
        v-else-if="isFileDocument(displayedDocument) && displayedDocument.url"
        :href="displayedDocument.url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex w-full text-5xl sm:text-7xl bg-gray-300 cursor-pointer justify-center items-center h-36 sm:h-44 max-w-full rounded-lg"
        :title="`Download ${displayedDocument.file_name}`"
      >
        <span class="fa fa-file-download"></span>
      </a>

      <div class="mt-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <SecondaryButton
          v-if="displayedDocument.url"
          type="button"
          class="w-full justify-center sm:w-auto"
          @click="openDocumentInNewTab"
        >
          {{ viewLabel() }}
        </SecondaryButton>
        <SecondaryButton type="button" class="w-full justify-center sm:w-auto" @click="openFilePicker">
          Replace
        </SecondaryButton>
        <SecondaryButton
          v-if="showExistingDocument"
          type="button"
          class="w-full justify-center sm:w-auto"
          @click="removeExisting"
        >
          Remove
        </SecondaryButton>
        <SecondaryButton
          v-else-if="pendingDocument"
          type="button"
          class="w-full justify-center sm:w-auto"
          @click="clearSelection"
        >
          Clear selection
        </SecondaryButton>
      </div>
    </figure>

    <div
      v-else-if="markedForRemoval"
      class="grid grid-cols-1 gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 sm:flex sm:flex-wrap sm:items-center"
    >
      <span class="text-sm text-amber-800 min-w-0">Document will be removed when you save.</span>
      <SecondaryButton type="button" class="w-full justify-center sm:w-auto" @click="undoRemove">
        Undo
      </SecondaryButton>
      <SecondaryButton type="button" class="w-full justify-center sm:w-auto" @click="openFilePicker">
        Upload replacement
      </SecondaryButton>
    </div>

    <div v-else class="space-y-2">
      <SecondaryButton type="button" class="w-full justify-center sm:w-auto" @click="openFilePicker">
        Choose file
      </SecondaryButton>
    </div>

    <InputError :message="licenseDocumentError" class="mt-2" />
    <InputError :message="removeLicenseDocumentError" class="mt-2" />
  </div>

  <DialogModal :show="previewModalOpen" max-width="7xl" @close="closePreviewModal">
    <template #title>
      <b v-text="displayedDocument?.file_name"></b>
    </template>

    <template #content>
      <img
        v-if="displayedDocument?.url"
        :src="displayedDocument.url"
        :alt="displayedDocument?.file_name"
        class="max-w-full h-auto"
      >
    </template>
  </DialogModal>
</template>
